import React, { useState, useEffect, useCallback } from 'react';
import { 
    Container, Row, Col, Card, CardHeader, CardBody, 
    Table, Button, Input, Label, UncontrolledDropdown, 
    DropdownToggle, DropdownMenu, Spinner,
    Modal, ModalHeader, ModalBody, UncontrolledCarousel,
    Form, FormGroup, Pagination, PaginationItem, PaginationLink,
    Nav, NavItem, NavLink
} from 'reactstrap';
import moment from 'moment';
import { APIClient } from '../../helpers/api_helper';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from '../../Components/Common/BreadCrumb';
import './users.css';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import Swal from 'react-bootstrap-sweetalert';

const DeviceLogsModal = ({ isOpen, toggle, userId }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        limit: 10,
        totalCount: 0
    });

    const fetchDeviceLogs = async (page = 1) => {
        try {
            setLoading(true);
            const offset = (page - 1) * pagination.limit;
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/python/userDeviceLogs/?set=${offset}&limit=${pagination.limit}&userId=${userId}`,
                {
                    headers: {
                        'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                    }
                }
            );

            const data = await response.json();
            if (!data.error) {
                setLogs(data.result);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalCount: data.recordsTotal
                }));
            }
        } catch (error) {
            console.error('Error fetching device logs:', error);
            toast.error('Failed to fetch device logs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && userId) {
            fetchDeviceLogs(1);
        }
    }, [isOpen, userId]);

    const handlePageChange = (page) => {
        fetchDeviceLogs(page);
    };

    const renderPaginationItems = () => {
        const totalPages = Math.ceil(pagination.totalCount / pagination.limit);
        let pages = [];

        if (totalPages <= 5) {
            pages = [...Array(totalPages)].map((_, i) => i + 1);
        } else {
            pages.push(1);
            let start = Math.max(2, pagination.currentPage - 1);
            let end = Math.min(totalPages - 1, pagination.currentPage + 1);

            if (start > 2) pages.push('...');
            for (let i = start; i <= end; i++) pages.push(i);
            if (end < totalPages - 1) pages.push('...');
            pages.push(totalPages);
        }

        return pages.map((page, index) => {
            if (page === '...') {
                return <PaginationItem key={`ellipsis_${index}`} disabled>
                    <PaginationLink href="#">...</PaginationLink>
                </PaginationItem>;
            }
            return (
                <PaginationItem key={page} active={page === pagination.currentPage}>
                    <PaginationLink onClick={() => handlePageChange(page)}>
                        {page}
                    </PaginationLink>
                </PaginationItem>
            );
        });
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>Device Logs</ModalHeader>
            <ModalBody>
                {loading ? (
                    <div className="text-center">
                        <Spinner color="primary" />
                    </div>
                ) : (
                    <>
                        <div className="table-responsive">
                            <Table className="table-striped table-nowrap mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Device</th>
                                        <th>App Version</th>
                                        <th>Location</th>
                                        <th>IP Address</th>
                                        <th>Session Duration</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log, index) => (
                                        <tr key={index}>
                                            <td>
                                                <div>{log.manufacturer} {log.model}</div>
                                                <small className="text-muted">{log.os} ({log.deviceToken})</small>
                                            </td>
                                            <td>{log.appVersion || 'N/A'}</td>
                                            <td>
                                                <div>{log.city}, {log.country}</div>
                                                <small className="text-muted">
                                                    {log.latitude}, {log.longitude}
                                                </small>
                                            </td>
                                            <td>{log.ipAddress}</td>
                                            <td>
                                                {moment.unix(log.sessionStart).format('DD MMM YYYY HH:mm')}
                                                <br />
                                                <small className="text-muted">
                                                    {moment.duration(log.sessionEnd - log.sessionStart, 'seconds').humanize()}
                                                </small>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        
                        {/* Pagination */}
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="pagination-info">
                                Showing {logs.length} of {pagination.totalCount} Records
                            </div>
                            <Pagination>
                                <PaginationItem disabled={pagination.currentPage === 1}>
                                    <PaginationLink previous onClick={() => handlePageChange(pagination.currentPage - 1)} />
                                </PaginationItem>
                                {renderPaginationItems()}
                                <PaginationItem disabled={pagination.currentPage === Math.ceil(pagination.totalCount / pagination.limit)}>
                                    <PaginationLink next onClick={() => handlePageChange(pagination.currentPage + 1)} />
                                </PaginationItem>
                            </Pagination>
                        </div>
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};

const UserCard = ({ user, selectedUsers, handleSelectUser }) => {
    const [logsModal, setLogsModal] = useState(false);
    const [tokenModal, setTokenModal] = useState(false);
    const navigate = useNavigate();

    const getProfilePicUrl = (profilePic) => {
        try {
            if (!profilePic) return require('../../assets/images/avatar.png');
            if (typeof profilePic === 'string') return profilePic;
            if (Array.isArray(profilePic) && profilePic.length > 0) {
                return `${process.env.REACT_APP_MEDIA_URL}${profilePic[0].url}`;
            }
            return require('../../assets/images/avatar.png');
        } catch (error) {
            return require('../../assets/images/avatar.png');
        }
    };

    const handleTokenTransaction = async (type, tokens) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/adminTokenTransaction`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                },
                body: JSON.stringify({
                    type,
                    noOfTokens: parseInt(tokens),
                    userId: user.id
                })
            });

            const data = await response.json();
            if (data.message) {
                toast.success(`Successfully ${type === 'CREDIT' ? 'added' : 'deducted'} ${tokens} tokens`);
                setTokenModal(false);
            }
        } catch (error) {
            toast.error('Failed to process token transaction');
        }
    };

    // Add click handler for the card
    const handleCardClick = (userId) => {
        navigate(`/user-details/${userId}/profile`);
        };

        return (
        <Col xl={3} lg={4} sm={6}>
            <Card className="user-card">
                <div 
                    className="clickable-card"
                    onClick={() => handleCardClick(user.id)}
                >
                    <CardBody>
                        {/* Header Section */}
                        <div className="d-flex align-items-start mb-3">
                            <div className="user-image-container">
                                <div className="image-wrapper">
                                    <img
                                        src={getProfilePicUrl(user.profilePic)}
                                        alt={user.username}
                                        className="user-avatar"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = require('../../assets/images/avatar.png');
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="flex-grow-1 ms-2">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h5 className="fs-16 mb-1">{`${user.firstName} ${user.lastName}`}</h5>
                                        <p className="text-muted mb-0">@{user.username}</p>
                                    </div>
                                    <div onClick={e => e.stopPropagation()}>
                                        <Input
                                            type="checkbox"
                                            checked={selectedUsers.includes(user.id)}
                                            onChange={() => handleSelectUser(user.id)}
                                        />
                                    </div>
                                </div>
                                <div className="mt-2 d-flex gap-1">
                                    <span className={`badge bg-${user.statusText === 'ACTIVE' ? 'success' : 'danger'}`}>
                                        {user.statusText}
                                    </span>
                                    <span className={`badge bg-${user.profileVerified ? 'success' : 'danger'}`}>
                                        {user.profileVerified ? 'Verified' : 'Not Verified'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Contact Info Section */}
                        <div className="user-info mb-3">
                            <Row className="g-2">
                                <Col xs={6}>
                                    <div className="info-item">
                                        <i className="ri-mail-line text-muted me-1"></i>
                                        <small>{user.email || 'N/A'}</small>
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <div className="info-item">
                                        <i className="ri-phone-line text-muted me-1"></i>
                                        <small>{`${user.countryCode} ${user.phoneNumber}` || 'N/A'}</small>
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <div className="info-item">
                                        <i className="ri-user-line text-muted me-1"></i>
                                        <small>{user.gender || 'N/A'}</small>
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <div className="info-item">
                                        <i className="ri-map-pin-line text-muted me-1"></i>
                                        <small>{user.city || 'N/A'}</small>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Stats Section */}
                        <div className="user-stats border-top pt-3">
                            <Row className="text-center g-0">
                                <Col xs={4} className="border-end">
                                    <div 
                                        className="p-2 clickable"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent card click
                                            navigate(`/user-details/${user.id}/referrals`);
                                        }}
                                    >
                                        <h6 className="mb-1">{user.referralCount}</h6>
                                        <small className="text-muted">Referrals</small>
                                    </div>
                                </Col>
                                <Col xs={4} className="border-end">
                                    <div className="p-2">
                                        <div className="d-flex align-items-center justify-content-center">
                                            <h6 className="mb-1 me-1">{user.tokenBalance}</h6>
                                <Button
                                                color="link" 
                                                className="p-0" 
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent card click
                                                    setTokenModal(true);
                                                }}
                                            >
                                                <i className="ri-add-circle-line"></i>
                                </Button>
                                        </div>
                                        <small className="text-muted">Tokens</small>
                                    </div>
                                </Col>
                                <Col xs={4}>
                                    <div 
                                        className="p-2 clickable"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent card click
                                            navigate(`/user-details/${user.id}/likes`);
                                        }}
                                    >
                                        <h6 className="mb-1">{user.likesCount}</h6>
                                        <small className="text-muted">Likes</small>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Logs Button */}
                        <div className="text-center mt-3">
                            <Button
                                color="soft-info"
                                size="sm"
                                className="w-100"
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent card click
                                    setLogsModal(true);
                                }}
                            >
                                <i className="ri-history-line align-bottom me-1"></i> View Logs
                            </Button>
                        </div>

                        {/* Modals */}
                        <DeviceLogsModal
                            isOpen={logsModal}
                            toggle={() => setLogsModal(false)}
                            userId={user.id}
                        />

                        <Modal isOpen={tokenModal} toggle={() => setTokenModal(false)}>
                            <ModalHeader toggle={() => setTokenModal(false)}>
                                Manage Tokens
                            </ModalHeader>
                            <ModalBody>
                                <Form onSubmit={(e) => {
                                    e.preventDefault();
                                    const type = e.target.type.value;
                                    const tokens = e.target.tokens.value;
                                    handleTokenTransaction(type, tokens);
                                }}>
                                    <FormGroup>
                                        <Label>Transaction Type</Label>
                                        <Input type="select" name="type" required>
                                            <option value="CREDIT">Credit</option>
                                            <option value="DEBIT">Debit</option>
                                        </Input>
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>Number of Tokens</Label>
                                        <Input 
                                            type="number" 
                                            name="tokens" 
                                            required 
                                            min="1"
                                        />
                                    </FormGroup>
                                    <Button color="primary" type="submit">
                                        Submit
                                    </Button>
                                </Form>
                            </ModalBody>
                        </Modal>
                    </CardBody>
                </div>
            </Card>
        </Col>
    );
};

// Add this new component for reported users
const ReportedUserCard = ({ user, selectedUsers, handleSelectUser }) => {
    const navigate = useNavigate();

    return (
        <Col xl={3} lg={4} sm={6}>
            <Card className="user-card">
                <div 
                    className="clickable-card"
                    onClick={() => navigate(`/user-details/${user._id}/profile`)}
                >
                    <CardBody>
                        <div className="d-flex align-items-start mb-3">
                            <div className="user-image-container">
                                <div className="image-wrapper">
                                    <img
                                        src={require('../../assets/images/avatar.png')}
                                        alt={user.username}
                                        className="user-avatar"
                                    />
                                </div>
                            </div>
                            <div className="flex-grow-1 ms-2">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h5 className="fs-16 mb-1">{`${user.firstName} ${user.lastName}`}</h5>
                                        <p className="text-muted mb-0">@{user.username}</p>
                                    </div>
                                    <div onClick={e => e.stopPropagation()}>
                                        <Input
                                            type="checkbox"
                                            checked={selectedUsers.includes(user._id)}
                                            onChange={() => handleSelectUser(user._id)}
                                        />
                                    </div>
                                </div>
                                <div className="mt-2">
                                    <span className="badge bg-danger">
                                        Reported {user.reportedCount} times
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="user-info mb-3">
                            <Row className="g-2">
                                <Col xs={6}>
                                    <div className="info-item">
                                        <i className="ri-mail-line text-muted me-1"></i>
                                        <small>{user.email || 'N/A'}</small>
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <div className="info-item">
                                        <i className="ri-phone-line text-muted me-1"></i>
                                        <small>{`${user.countryCode} ${user.phoneNumber}` || 'N/A'}</small>
                                    </div>
                </Col>
            </Row>
                        </div>

                        <div className="text-muted small">
                            <i className="ri-time-line me-1"></i>
                            Reported on: {moment(user.reportedTs).format('DD MMM YYYY, hh:mm A')}
                        </div>
                    </CardBody>
                </div>
            </Card>
        </Col>
    );
};

const Users = () => {
    document.title = "Users";
    
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [filters, setFilters] = useState({
        profileStatus: '',
        searchQuery: ''
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        limit: 16,
        totalCount: 0
    });
    const [searchText, setSearchText] = useState('');
    const [suspendModal, setSuspendModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [actionReason, setActionReason] = useState('');
    const [activeTab, setActiveTab] = useState('active');
    const [activateModal, setActivateModal] = useState(false);

    const fetchUsers = async (page = 1, searchQuery = '') => {
        try {
            setLoading(true);
            const offset = (page - 1) * pagination.limit;
            const baseUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, '');
            let url;

            if (activeTab === 'reported') {
                url = `${baseUrl}/report?userType=1&limit=${pagination.limit}&offset=${offset}&type=2`;
            } else {
                url = `${baseUrl}/python/users/?userType=1&limit=${pagination.limit}&offset=${offset}`;
                
                // Add status based on active tab
                switch (activeTab) {
                    case 'active':
                        url += '&status=1';
                        break;
                    case 'suspended':
                        url += '&status=2';
                        break;
                    case 'deleted':
                        url += '&status=4';
                        break;
                    default:
                        url += '&status=1';
                }

                // Add profile status filter
                if (filters.profileStatus !== '') {
                    url += `&profileStatus=${filters.profileStatus}`;
                }

                // Add search query
                if (searchQuery) {
                    url += `&searchedKey=${searchQuery}`;
                }
            }
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                }
            });

            const data = await response.json();
            if (activeTab === 'reported') {
                if (data.message === "Success") {
                    setUsers(data.data.data || []);
                    setPagination(prev => ({
                        ...prev,
                        currentPage: page,
                        totalCount: data.data.totalCount || 0
                    }));
                }
            } else {
                if (!data.error) {
                    setUsers(data.result || []);
                    setPagination(prev => ({
                        ...prev,
                        currentPage: page,
                        totalCount: data.recordsTotal || 0
                    }));
                } else {
                    throw new Error(data.message);
                }
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error("Failed to fetch users");
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers(1);
    }, [filters, activeTab]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSearch = (e) => {
        const query = e.target.value;
        setSearchText(query);
        fetchUsers(1, query);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedUsers(users.map(user => user.id));
        } else {
            setSelectedUsers([]);
        }
    };

    const handleSelectUser = (userId) => {
        setSelectedUsers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    const handlePageChange = (page) => {
        fetchUsers(page);
    };

    const handleSuspendUsers = () => {
        if (selectedUsers.length === 0) {
            toast.warning('Please select users to suspend');
            return;
        }
        setSuspendModal(true);
    };

    const handleDeleteUsers = () => {
        if (selectedUsers.length === 0) {
            toast.warning('Please select users to delete');
            return;
        }
        setDeleteModal(true);
    };

    const handleActivateUsers = () => {
        if (selectedUsers.length === 0) {
            toast.warning('Please select users to activate');
            return;
        }
        handleUserStatusUpdate('1').then(success => {
            if (success) {
                toast.success('Users activated successfully');
            }
        });
    };

    const handleUserStatusUpdate = async (status, reason = '') => {
        try {
            setLoading(true);
            const baseUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, '');
            
            // Create payload based on status
            const payload = {
                status,
                userIds: selectedUsers.map(id => activeTab === 'reported' ? id : id)
            };

            // Only add reason for suspend and delete actions
            if (status === '2' || status === '4') {
                payload.reason = reason;
            }
            
            const response = await fetch(`${baseUrl}/user/account`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 200) {
                toast.success(`Users ${
                    status === '1' ? 'activated' : 
                    status === '2' ? 'suspended' : 
                    'deleted'
                } successfully`, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                });
                setSelectedUsers([]); // Clear selection
                
                // Close modals if open
                setSuspendModal(false);
                setDeleteModal(false);
                setActionReason('');
                
                // Refresh the users list
                await fetchUsers(pagination.currentPage);
                
                return true;
            } else {
                throw new Error('Operation failed');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to update user status', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
            console.error(error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const renderPaginationItems = () => {
        const totalPages = Math.ceil(pagination.totalCount / pagination.limit);
        let pages = [];

        if (totalPages <= 5) {
            pages = [...Array(totalPages)].map((_, i) => i + 1);
        } else {
            pages.push(1);
            let start = Math.max(2, pagination.currentPage - 1);
            let end = Math.min(totalPages - 1, pagination.currentPage + 1);

            if (start > 2) pages.push('...');
            for (let i = start; i <= end; i++) pages.push(i);
            if (end < totalPages - 1) pages.push('...');
            pages.push(totalPages);
        }

        return pages.map((page, index) => {
            if (page === '...') {
                return <PaginationItem key={`ellipsis_${index}`} disabled>
                    <PaginationLink href="#">...</PaginationLink>
                </PaginationItem>;
            }
            return (
                <PaginationItem key={page} active={page === pagination.currentPage}>
                    <PaginationLink onClick={() => handlePageChange(page)}>
                        {page}
                    </PaginationLink>
                </PaginationItem>
            );
        });
    };

    return (
        <div className="page-content">
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
            <Container fluid>
                <BreadCrumb title="Users" pageTitle="Management" />
                <Row className="mb-3">
                    <Col>
                        <Nav tabs>
                            <NavItem>
                                <NavLink
                                    className={activeTab === 'active' ? 'active' : ''}
                                    onClick={() => setActiveTab('active')}
                                >
                                    Active Users
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={activeTab === 'suspended' ? 'active' : ''}
                                    onClick={() => setActiveTab('suspended')}
                                >
                                    Suspended Users
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={activeTab === 'deleted' ? 'active' : ''}
                                    onClick={() => setActiveTab('deleted')}
                                >
                                    Deleted Users
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={activeTab === 'reported' ? 'active' : ''}
                                    onClick={() => setActiveTab('reported')}
                                >
                                    Reported Users
                                </NavLink>
                            </NavItem>
                        </Nav>
                    </Col>
                </Row>
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader>
                                <Row className="g-3">
                                    {/* Title - Full width on mobile */}
                                    <Col xs={12} sm={4}>
                                        <h5 className="card-title mb-0">Users List</h5>
                                    </Col>

                                    {/* Search - Full width on mobile */}
                                    <Col xs={12} sm={4}>
                                        <Form onSubmit={handleSearch}>
                                            <div className="search-box">
                                                <Input
                                                    type="text"
                                                    className="form-control search"
                                                    placeholder="Search users..."
                                                    value={searchText}
                                                    onChange={handleSearch}
                                                />
                                                <i className="ri-search-line search-icon"></i>
                                            </div>
                                        </Form>
                                    </Col>

                                    {/* Action Buttons - Full width on mobile */}
                                    <Col xs={12} sm={4}>
                                        <div className="d-flex gap-2 justify-content-end action-buttons">
                                            {activeTab === 'active' && (
                                                <>
                                                    <Button
                                                        color="danger"
                                                        className="btn-soft-danger d-flex align-items-center"
                                                        onClick={handleSuspendUsers}
                                                        disabled={selectedUsers.length === 0}
                                                    >
                                                        <i className="ri-user-unfollow-line"></i>
                                                        <span className="ms-1">Suspend</span>
                                                    </Button>
                                                    <Button
                                                        color="danger"
                                                        className="d-flex align-items-center"
                                                        onClick={handleDeleteUsers}
                                                        disabled={selectedUsers.length === 0}
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                        <span className="ms-1">Delete</span>
                                                    </Button>
                                                </>
                                            )}
                                            {activeTab === 'suspended' && (
                                                <>
                                                    <Button
                                                        color="success"
                                                        className="d-flex align-items-center"
                                                        onClick={handleActivateUsers}
                                                        disabled={selectedUsers.length === 0}
                                                    >
                                                        <i className="ri-user-follow-line"></i>
                                                        <span className="ms-1">Activate</span>
                                                    </Button>
                                                    <Button
                                                        color="danger"
                                                        className="d-flex align-items-center"
                                                        onClick={handleDeleteUsers}
                                                        disabled={selectedUsers.length === 0}
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                        <span className="ms-1">Delete</span>
                                                    </Button>
                                                </>
                                            )}
                                            {activeTab === 'reported' && (
                                                <>
                                                    <Button
                                                        color="danger"
                                                        className="btn-soft-danger d-flex align-items-center"
                                                        onClick={handleSuspendUsers}
                                                        disabled={selectedUsers.length === 0}
                                                    >
                                                        <i className="ri-user-unfollow-line"></i>
                                                        <span className="ms-1">Suspend</span>
                                                    </Button>
                                                    <Button
                                                        color="danger"
                                                        className="d-flex align-items-center"
                                                        onClick={handleDeleteUsers}
                                                        disabled={selectedUsers.length === 0}
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                        <span className="ms-1">Delete</span>
                                                    </Button>
                                                </>
                                            )}
                                    <UncontrolledDropdown>
                                                <DropdownToggle tag="button" className="btn btn-soft-info d-flex align-items-center">
                                                    <i className="ri-filter-3-line"></i>
                                                    <span className="ms-1">Filter</span>
                                        </DropdownToggle>
                                                <DropdownMenu className="p-3">
                                                    <FormGroup>
                                                        <Label>Profile Status</Label>
                                                <Input 
                                                    type="select"
                                                            value={filters.profileStatus}
                                                            onChange={(e) => handleFilterChange('profileStatus', e.target.value)}
                                                >
                                                    <option value="">All</option>
                                                            <option value="1">Verified</option>
                                                            <option value="0">Not Verified</option>
                                                </Input>
                                                    </FormGroup>
                                        </DropdownMenu>
                                    </UncontrolledDropdown>
                                </div>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody>
                                <Row className="g-3">
                                    {loading ? (
                                        <Col className="text-center">
                                            <Spinner color="primary" />
                                        </Col>
                                    ) : users && users.length > 0 ? (
                                        users.map((user) => (
                                            activeTab === 'reported' ? (
                                                <ReportedUserCard
                                                    key={user._id}
                                                    user={user}
                                                    selectedUsers={selectedUsers}
                                                    handleSelectUser={handleSelectUser}
                                                />
                                            ) : (
                                                <UserCard 
                                                    key={user.id}
                                                    user={user}
                                                    selectedUsers={selectedUsers}
                                                    handleSelectUser={handleSelectUser}
                                                />
                                            )
                                        ))
                                    ) : (
                                        <Col className="text-center">
                                            <div className="p-3">
                                                <p className="text-muted">
                                                    {activeTab === 'active' && "No active users found"}
                                                    {activeTab === 'suspended' && "No suspended users found"}
                                                    {activeTab === 'deleted' && "No deleted users found"}
                                                    {activeTab === 'reported' && "No reported users found"}
                                                </p>
                                                    </div>
                                        </Col>
                                    )}
                                </Row>
                                {users && users.length > 0 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="pagination-info">
                                            Showing {users.length} of {pagination.totalCount} Results
                                                    </div>
                                        <Pagination>
                                            <PaginationItem disabled={pagination.currentPage === 1}>
                                                <PaginationLink previous onClick={() => handlePageChange(pagination.currentPage - 1)} />
                                            </PaginationItem>
                                            {renderPaginationItems()}
                                            <PaginationItem disabled={pagination.currentPage === Math.ceil(pagination.totalCount / pagination.limit)}>
                                                <PaginationLink next onClick={() => handlePageChange(pagination.currentPage + 1)} />
                                            </PaginationItem>
                                        </Pagination>
                                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Suspend Modal */}
            <Modal isOpen={suspendModal} toggle={() => setSuspendModal(false)}>
                <ModalHeader toggle={() => setSuspendModal(false)}>
                    Suspend Users
                </ModalHeader>
                <ModalBody>
                    <Form onSubmit={async (e) => {
                        e.preventDefault();
                        if (!actionReason) {
                            toast.error('Please enter a reason for suspension');
                            return;
                        }
                        await handleUserStatusUpdate('2', actionReason);
                    }}>
                        <FormGroup>
                            <Label>Reason for Suspension</Label>
                            <Input
                                type="textarea"
                                value={actionReason}
                                onChange={(e) => setActionReason(e.target.value)}
                                placeholder="Enter reason for suspension"
                                required
                            />
                        </FormGroup>
                        <div className="d-flex gap-2 justify-content-end mt-4">
                            <Button color="light" onClick={() => setSuspendModal(false)}>
                                Cancel
                            </Button>
                            <Button color="danger" type="submit">
                                Suspend Users
                            </Button>
                                </div>
                    </Form>
                </ModalBody>
            </Modal>

            {/* Delete Modal */}
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
                <ModalHeader toggle={() => setDeleteModal(false)}>
                    Delete Users
                </ModalHeader>
                <ModalBody>
                    <div className="alert alert-warning">
                        <i className="ri-alert-line me-2"></i>
                        This action cannot be undone. Selected users will be permanently deleted.
                                            </div>
                    <Form onSubmit={async (e) => {
                        e.preventDefault();
                        if (!actionReason) {
                            toast.error('Please enter a reason for deletion');
                            return;
                        }
                        await handleUserStatusUpdate('4', actionReason);
                    }}>
                        <FormGroup>
                            <Label>Reason for Deletion</Label>
                            <Input
                                type="textarea"
                                value={actionReason}
                                onChange={(e) => setActionReason(e.target.value)}
                                placeholder="Enter reason for deletion"
                                required
                            />
                        </FormGroup>
                        <div className="d-flex gap-2 justify-content-end mt-4">
                            <Button color="light" onClick={() => setDeleteModal(false)}>
                                Cancel
                            </Button>
                            <Button color="danger" type="submit">
                                Delete Users
                            </Button>
                                            </div>
                    </Form>
                </ModalBody>
            </Modal>

            {loading && (
                <div className="overlay">
                    <Spinner color="primary" />
                </div>
            )}
        </div>
    );
};

export default Users;